import { clip, prettyErrors } from "./logging";
import { parseError, isParseError } from "./errors";
import { subLatexTerms } from "./latexSub";
export const parse = (text) => {
    if (!text.length)
        throw new Error(`I've no content to parse - text is empty!`);
    const errors = [];
    const warns = [];
    let location = 0;
    let char = text[0];
    const stringAbbrvs = {};
    const eat = (chars) => {
        let found = 0;
        while (chars.includes(char)) {
            next();
            found++;
        }
        return { eat: true, chars, found };
    };
    const eatWhitespace = () => {
        const skip = [' ', '\t', '\r', '\n', '\b'];
        return eat(skip);
    };
    const next = (expect) => {
        if (expect) {
            if (!text.slice(location, location + expect.length).startsWith(expect))
                throw parseError(`I was expecting "${expect.replace('\n', '\\n')}" but I found "${clip(text.slice(location, location + expect.length))}".`, location);
        }
        location += (expect === null || expect === void 0 ? void 0 : expect.length) || 1;
        char = text[location];
        return char;
    };
    const regexNext = (regex, ahead, msgGen) => {
        const input = text.slice(location, location + ahead);
        const match = input.match(regex);
        if (!match)
            throw parseError(msgGen(regex + "", text.slice(location)), location);
        next(match[0]);
        return match[0];
    };
    const peek = (length) => text.slice(location, location + length);
    const isEof = () => location === text.length;
    const skipToNextNl = () => {
        while (!isEof() && char !== '\n')
            next();
        next('\n');
    };
    const matchBibItem = () => {
        const bibItem = {
            entries: [],
        };
        while (!isEof()) {
            const isCommentEntry = () => { var _a; return ((_a = peek('@comment'.length)) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === '@comment'; };
            const isPreambleEntry = () => { var _a; return ((_a = peek('@preamble'.length)) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === '@preamble'; };
            const isStringAbbrvEntry = () => { var _a; return ((_a = peek('@string'.length)) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === '@string'; };
            if (isCommentEntry()) {
                skipToNextNl();
            }
            else if (isPreambleEntry()) {
                skipToNextNl();
            }
            else if (isStringAbbrvEntry()) {
                matchStringAbbrvEntry();
            }
            else if (char === '@') {
                next();
                try {
                    const bibEntryMatch = matchBibEntry();
                    if (bibEntryMatch)
                        bibItem.entries.push(bibEntryMatch);
                }
                catch (e) {
                    if (!isParseError(e))
                        throw e;
                    errors.push(e);
                }
            }
            else {
                next();
            }
        }
        return bibItem;
    };
    const matchStringAbbrvEntry = () => {
        next('@string');
        eatWhitespace();
        next('{');
        const tag = matchTag();
        eatWhitespace();
        next('}');
        stringAbbrvs[tag.fieldName] = tag.fieldValue;
    };
    const matchBibEntry = () => {
        const locationAtTypeMatch = location;
        const type = matchBibType();
        if (!type || !type.length) {
            warns.push({
                message: `I was expecting bib type while parsing bib entry.`,
                location: locationAtTypeMatch,
            });
            return;
        }
        eatWhitespace();
        next('{');
        const citationKey = matchCitationKey();
        regexNext(/[,\n]/, 1, (expected, found) => `I was expecting to find either "," or newline as end of citekey.`);
        eatWhitespace();
        const tags = [];
        let tagMatch;
        tagMatch = matchTag();
        if (tagMatch)
            tags.push(tagMatch);
        while (peek(1) === ',') {
            next(',');
            eatWhitespace();
            if (char === '}')
                break;
            tagMatch = matchTag();
            if (tagMatch)
                tags.push(tagMatch);
        }
        eatWhitespace();
        next('}');
        return {
            type,
            citationKey,
            tags,
            startLocation: locationAtTypeMatch,
            endLocation: location,
        };
    };
    const matchTag = () => {
        eatWhitespace();
        const startLocation = location;
        const fieldName = matchFieldName();
        eatWhitespace();
        next('=');
        eatWhitespace();
        const fieldValue = matchFieldValue();
        return {
            startLocation,
            fieldName,
            fieldValue,
        };
    };
    const matchFieldName = () => {
        const fieldNameChars = /[_:a-zA-Z0-9-]/;
        let fieldName = '';
        while (!isEof() && fieldNameChars.test(char)) {
            fieldName += char;
            next();
        }
        fieldName = fieldName.toLowerCase();
        return fieldName;
    };
    const matchFieldValue = () => {
        let fieldValue = '';
        do {
            if (char === '#') {
                next('#');
                eatWhitespace();
            }
            if (char === '"') {
                fieldValue += matchQuotationField();
            }
            else if (char === "{") {
                fieldValue += matchBraceExp();
            }
            else if (/[_A-z0-9]/.test(char)) {
                fieldValue += matchNonenclosedField();
            }
            eatWhitespace();
        } while (!isEof() && peek(1) === '#');
        return fieldValue;
    };
    const matchNonenclosedField = () => {
        const allowedChars = /[_A-z0-9]/;
        let content = '';
        while (!isEof() && allowedChars.test(char)) {
            content += char;
            next();
        }
        if (/\d+/.test(content))
            return content;
        const stringAbbrv = stringAbbrvs[content];
        if (!stringAbbrv) {
            warns.push({
                message: `I found no string abbreviation for ${content}.`,
                location,
            });
            return "";
        }
        return stringAbbrv;
    };
    const matchQuotationField = () => {
        next('"');
        let content = '';
        while (!isEof()) {
            if (char === '{') {
                content += matchBraceExp();
                continue;
            }
            if (char === '}')
                throw parseError("Missing starting brace", location);
            if (char === '"') {
                next('"');
                break;
            }
            content += char;
            next();
        }
        return content;
    };
    const matchBraceExp = () => {
        let braces = [];
        let content = '';
        let lastChar = '';
        while (!isEof()) {
            if (lastChar !== '\\' && char === '{') {
                braces.push('{');
                lastChar = char;
                next();
                continue;
            }
            if (lastChar !== '\\' && char === '}') {
                if (braces.length === 0)
                    throw parseError("Unmatched brace.", location);
                braces.pop();
                lastChar = char;
                next();
                if (braces.length === 0)
                    break;
                else
                    continue;
            }
            content += char;
            lastChar = char;
            next();
        }
        if (braces.length)
            throw parseError("Unmatched brace.", location);
        return content;
    };
    const matchCitationKey = () => {
        const keyChars = /[^\t\r\n,]/;
        let key = '';
        while (!isEof() && keyChars.test(char)) {
            key += char;
            next();
        }
        return key;
    };
    const matchBibType = () => {
        const bibTypeChars = /[_A-Za-z]/;
        let type = '';
        while (!isEof() && bibTypeChars.test(char)) {
            type += char;
            next();
        }
        return type;
    };
    return {
        bibItem: subLatexTerms(matchBibItem()),
        errors,
        warns,
        prettyErrors: prettyErrors(text, errors),
        prettyWarns: prettyErrors(text, warns),
    };
};
